if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      disconnectedCallback() {
        if (this.addAttempt && !this.addAttempt.handoff) this.addAttempt.cancelled = true;
      }

      isCurrentAttempt(attempt) {
        return this.addAttempt === attempt && !attempt.cancelled && (this.isConnected || attempt.handoff);
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const cart = this.cart;
        const modal = this.closest('quick-add-modal');
        const drawer = typeof cart?.refreshContents === 'function' ? cart : null;
        const attempt = this.addAttempt = {
          drawer, revision: drawer?.invalidateRender(), modal, opener: modal?.openedBy,
          form: this.form, accepted: false, dispatched: false, blocked: false,
        };
        this.inspectionNotice?.remove();
        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          const formData = new FormData(attempt.form);
          if (cart) {
            formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
          attempt.dispatched = true;
          const receipt = await fetch(`${routes.cart_add_url}`, config);
          attempt.httpStatus = receipt.status;
          attempt.httpOK = receipt.ok;
          const response = await receipt.json();
          const usableLine = (line) => line && Number(line.id) > 0 && typeof line.key === 'string' && line.key.length > 0 &&
            Number.isInteger(line.quantity) && line.quantity > 0;
          if (!receipt.ok || response?.status) {
            attempt.outcome = 'rejected';
            try {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form', productVariantId: formData.get('id'),
                errors: response?.errors || response?.description, message: response?.message,
              }).catch(() => {});
            } catch {}
            if (this.isCurrentAttempt(attempt)) {
              this.handleErrorMessage(response?.description);
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if ((receipt.status === 422 || response?.status === 422) && soldOutMessage) {
                attempt.soldOut = true;
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
            }
            await this.inspectAdd(attempt);
            return;
          }
          if (!usableLine(response) && !(Array.isArray(response?.items) && response.items.length && response.items.every(usableLine))) {
            throw new Error('cart_acknowledgement_unavailable');
          }

          attempt.accepted = true;
          attempt.outcome = 'accepted';
          let marker;
          try { marker = CartPerformance.createStartingMarker('add:wait-for-subscribers'); } catch {}
          try {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form', productVariantId: formData.get('id'), cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', marker);
            }).catch(() => {});
          } catch {}

          const paint = () => {
            if (!this.isCurrentAttempt(attempt)) return;
            if (drawer && !drawer.isCurrentRender(attempt.revision)) return this.showInspection(attempt);
            if (!cart || (!drawer && !usableLine(response))) return this.showInspection(attempt);
            let paintMarker;
            try { paintMarker = CartPerformance.createStartingMarker('add:paint-updated-sections'); } catch {}
            try {
              Promise.resolve(cart.renderContents(response, true, true, {
                outcome: 'accepted', isCurrent: () => this.isCurrentAttempt(attempt),
              })).catch(() => this.showInspection(attempt));
            } catch { this.showInspection(attempt); }
            finally {
              try { if (paintMarker) CartPerformance.measureFromMarker('add:paint-updated-sections', paintMarker); } catch {}
            }
          };
          if (!this.isCurrentAttempt(attempt)) return;
          if (attempt.modal) {
            attempt.handoff = true;
            attempt.modal.hide(true);
            attempt.modal.addEventListener('product-info:loaded', () => { attempt.cancelled = true; }, { once: true });
            setTimeout(() => {
              try { paint(); } catch { this.showInspection(attempt); }
            });
          } else paint();
        } catch (error) {
          if (error?.pcHandled) {
            attempt.outcome = 'refused';
            if (this.isCurrentAttempt(attempt)) this.handleErrorMessage();
          } else if (attempt.accepted) {
            this.showInspection(attempt);
          } else if (attempt.dispatched) {
            attempt.outcome = attempt.httpOK === false ? 'rejected' : 'unknown';
            await this.inspectAdd(attempt);
          } else {
            attempt.outcome = 'refused';
            if (this.isCurrentAttempt(attempt)) this.handleErrorMessage(window.cartStrings?.error);
          }
        } finally {
          if (this.addAttempt === attempt) {
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            if (!attempt.blocked) this.submitButton.removeAttribute('aria-disabled');
          }
          try { CartPerformance.measureFromEvent('add:user-action', evt); } catch {}
        }
      }

      async inspectAdd(attempt) {
        if (this.addAttempt !== attempt) return;
        attempt.blocked = true;
        const current = () => this.isCurrentAttempt(attempt);
        const displayed = attempt.onDisplayed = () => {
          if (!current() || attempt.soldOut) return;
          attempt.blocked = false;
          this.submitButton.removeAttribute('aria-disabled');
        };
        this.showInspection(attempt);
        if (attempt.modal || !current() || !attempt.drawer?.isCurrentRender(attempt.revision)) return;
        const context = { outcome: attempt.outcome, isCurrent: current, onDisplayed: displayed };
        try {
          await attempt.drawer.refreshContents({}, attempt.revision, context);
        } catch {
          if (current() && attempt.drawer.isCurrentRender(attempt.revision)) attempt.drawer.showRenderRecovery({}, context);
        }
      }

      showInspection(attempt) {
        if (!this.isCurrentAttempt(attempt)) return;
        this.inspectionNotice?.remove();
        const notice = document.createElement('div');
        notice.className = 'pcn-note pcn-note--recovery';
        notice.setAttribute('role', 'status');
        const message = document.createElement('p');
        message.textContent = attempt.accepted
          ? 'Your item was added. View your cart to check your items.'
          : 'We could not confirm this add. Check your cart before adding again.';
        const link = document.createElement('a');
        link.className = 'pcn-act';
        link.href = routes.cart_url;
        link.textContent = 'View cart';
        link.addEventListener('click', (event) => {
          const drawer = document.querySelector('cart-drawer');
          if (!drawer?.isConnected || typeof drawer.viewCart !== 'function') return;
          event.preventDefault();
          if (attempt.modal?.hasAttribute('open')) attempt.modal.hide(true);
          const opener = attempt.opener?.isConnected ? attempt.opener : this.submitButton;
          drawer.viewCart(opener, { outcome: attempt.outcome, onDisplayed: attempt.onDisplayed });
        });
        notice.append(message, link);
        if (!this.isConnected || attempt.handoff) {
          const opener = attempt.opener;
          if (opener?.isConnected && opener.getClientRects().length && !opener.closest('quick-add-modal, [hidden], [inert]')) {
            opener.insertAdjacentElement('afterend', notice);
          } else {
            (document.getElementById('MainContent') || document.body).prepend(notice);
          }
          if (!attempt.drawer || attempt.drawer.isCurrentRender(attempt.revision)) {
            if (document.activeElement === document.body && notice.previousElementSibling === opener) {
              try { opener.focus({ preventScroll: true }); } catch {}
            }
            try { notice.scrollIntoView?.({ block: 'nearest' }); } catch {}
          }
        } else this.append(notice);
        this.inspectionNotice = notice;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
